/**
 * Live Update Service
 * 
 * Supabase Storage üzerinden self-hosted OTA (Over-The-Air) güncelleme sistemi.
 * Capawesome Live Update plugin'i ile çalışır.
 * 
 * Akış:
 * 1. Uygulama açılışında manifest.json kontrol edilir
 * 2. Yeni versiyon varsa arka planda indirilir
 * 3. Sonraki açılışta otomatik uygulanır
 */
import { Capacitor } from '@capacitor/core'
import { captureError, captureWarning } from '@/lib/sentry'
// Bu ikisi `import.meta.env` okuyor; ayrı modülde durmasalardı bu servis
// test ortamında import EDİLEMEZDİ. Bkz. lib/liveUpdateConfig.ts
import { MANIFEST_URL, APP_VERSION } from '@/lib/liveUpdateConfig'

/** Manifest yapısı — Supabase Storage'daki manifest.json dosyası */
interface UpdateManifest {
  /** Güncel bundle versiyonu (semantic versioning) */
  version: string
  /** Bundle zip dosyasının URL'i */
  url: string
  /** Zorunlu güncelleme mi? */
  mandatory?: boolean
  /** Minimum native versiyon (bu altında olan native build'ler güncellenmez) */
  minNativeVersion?: string
}

/** Plugin'i dinamik import ile yükle — web'de gereksiz yük oluşturmaz */
async function getLiveUpdatePlugin() {
  const mod = await import('@capawesome/capacitor-live-update')
  
  // CRITICAL FIX: Capacitor plugin proxies trigger 'not implemented' on native 
  // when `.then` is accessed by JS engines (Promise checks) or Sentry error capture.
  // We wrap it in another Proxy to safely return `undefined` for `.then`.
  const safeLiveUpdate = new Proxy(mod.LiveUpdate, {
    get(target, prop) {
      if (prop === 'then') return undefined
      return target[prop as keyof typeof target]
    }
  })

  return { plugin: safeLiveUpdate }
}

/**
 * Mevcut bundle ID'yi döndürür.
 * Eğer hiç güncelleme yapılmamışsa undefined döner (orijinal bundle).
 */
export async function getCurrentBundle(): Promise<string | undefined> {
  try {
    const { plugin: LiveUpdate } = await getLiveUpdatePlugin()
    const result = await LiveUpdate.getBundles()
    // Aktif bundle listesindeki son eleman mevcut versiyondur
    const bundles = result.bundleIds || []
    return bundles.length > 0 ? bundles[bundles.length - 1] : undefined
  } catch {
    return undefined
  }
}

/**
 * Sunucudaki manifest.json'ı kontrol eder.
 * Network hatası durumunda sessizce null döner.
 */
async function fetchManifest(): Promise<UpdateManifest | null> {
  try {
    const response = await fetch(`${MANIFEST_URL}?t=${Date.now()}`, {
      cache: 'no-store',
    })
    if (!response.ok) return null
    return await response.json()
  } catch {
    return null
  }
}

/**
 * Versiyon karşılaştırması (semantic versioning).
 * true döner eğer remote > local ise.
 */
function isNewerVersion(remote: string, local: string): boolean {
  const r = remote.split('.').map(Number)
  const l = local.split('.').map(Number)

  for (let i = 0; i < 3; i++) {
    const rv = r[i] || 0
    const lv = l[i] || 0
    if (rv > lv) return true
    if (rv < lv) return false
  }
  return false
}

/**
 * Package.json'daki native build versiyonu.
 * Vite build sırasında VITE_APP_VERSION env ile inject edilir.
 */
function getAppVersion(): string {
  return APP_VERSION
}

let _lastCheckTime = 0
const CHECK_MIN_INTERVAL = 15 * 60 * 1000 // 15 dakika en az aralık
let _updateFailCount = 0
const MAX_UPDATE_FAIL_LOGS = 2

/**
 * Live Update kontrolü ve indirme.
 * Uygulama açılışında çağrılır (main.tsx veya App.tsx).
 * 
 * - Web/PWA'da hiçbir şey yapmaz
 * - Native'de manifest kontrol eder, yeni versiyon varsa arka planda indirir
 * - İndirilen bundle bir sonraki açılışta otomatik uygulanır
 */
export async function checkForUpdate(): Promise<void> {
  // Sadece native platformlarda çalış
  if (!Capacitor.isNativePlatform()) return

  // Tekrar eden agrasif kontrolleri engelle (15 dk throttle)
  if (Date.now() - _lastCheckTime < CHECK_MIN_INTERVAL && _updateFailCount === 0) {
     const nextCheckMin = Math.round((CHECK_MIN_INTERVAL - (Date.now() - _lastCheckTime)) / 60000)
     return
  }
  
  _lastCheckTime = Date.now()

  try {
    const { plugin: LiveUpdate } = await getLiveUpdatePlugin()

    // 1. Manifest'i kontrol et
    const manifest = await fetchManifest()
    if (!manifest?.version || !manifest?.url) {
      return
    }

    // 2. Mevcut bundle versiyonu
    const currentBundle = await getCurrentBundle()
    const currentVersion = currentBundle || getAppVersion()

    // 3. Yeni versiyon var mı?
    if (!isNewerVersion(manifest.version, currentVersion)) {
      _updateFailCount = 0 // Başarılı olunca fail sayacını sıfırla
      return
    }

    // 4. Bundle'ı indir
    await LiveUpdate.downloadBundle({
      url: manifest.url,
      bundleId: manifest.version,
    })

    // 5. İndirilen bundle'ı bir sonraki başlatmada kullanılacak şekilde ayarla
    await LiveUpdate.setNextBundle({ bundleId: manifest.version })
    _updateFailCount = 0 // Başarılı

    // 6. Eğer zorunlu güncelleme ise hemen reload et
    if (manifest.mandatory) {
      await LiveUpdate.reload()
    }
  } catch (error: any) {
    const errMessage = error?.message || String(error)
    if (errMessage.includes('bundle already exists')) {
      _updateFailCount = 0
      return
    }

    _updateFailCount++
    if (_updateFailCount <= MAX_UPDATE_FAIL_LOGS) {
      captureWarning('Güncelleme kontrolü başarısız', { service: 'liveUpdateService', action: 'checkForUpdate', error, failCount: _updateFailCount })
    }
  }
}

/**
 * Başarılı yüklenmeyi onayla (rollback koruması).
 * Uygulama başarıyla açıldıktan sonra çağrılır.
 * Bu çağrılmazsa, hatalı bir bundle sonraki açılışta orijinaline geri döner.
 */
export async function confirmUpdate(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return

  try {
    const { plugin: LiveUpdate } = await getLiveUpdatePlugin()
    await LiveUpdate.ready()
  } catch {
    // Sessiz — ilk kurulumda veya orijinal bundle'da hata verebilir
  }
}

/**
 * Orijinal bundle'a geri dön (acil durum).
 * Hatalı bir güncelleme durumunda kullanılır.
 */
export async function resetToOriginal(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return

  try {
    const { plugin: LiveUpdate } = await getLiveUpdatePlugin()
    await LiveUpdate.reset()
    await LiveUpdate.reload()
  } catch (error) {
    captureError(error, { service: 'liveUpdateService', action: 'resetToOriginal' })
  }
}
